// ─── GBCOSPACK — Quotation Document Label Dictionary ─────────────────────────
// Fixed UI labels per document locale.
// Document locale is INDEPENDENT from the Admin UI locale.

import type { DocumentLocale } from './types';

export interface QuotationLabels {
  documentTitle: {
    preliminary_quote: string;
    final_offer: string;
  };
  quotationNo: string;
  referenceNo: string;
  date: string;
  validUntil: string;
  seller: string;
  buyer: string;
  contactPerson: string;
  address: string;
  country: string;
  telephone: string;
  email: string;
  website: string;

  // Table columns
  no: string;
  product: string;
  sku: string;
  specification: string;
  material: string;
  capacity: string;
  category: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  finishing: string;

  // Totals
  productAmount: string;
  finishingAmount: string;
  subtotal: string;
  freight: string;
  customsClearance: string;
  insurance: string;
  otherCharges: string;
  total: string;

  // Terms section
  paymentTerms: string;
  incoterm: string;
  namedPlace: string;
  leadTime: string;
  remarks: string;
  specialRequirements: string;

  // Misc
  currency: string;
  exchangeRate: string;
  noImage: string;
  authorizedBy: string;
  position: string;
  issuedDate: string;
  pageOf: string;
  pendingConfirmation: string;
}

const en: QuotationLabels = {
  documentTitle: {
    preliminary_quote: 'PRELIMINARY QUOTATION',
    final_offer: 'FINAL OFFER',
  },
  quotationNo: 'Quotation No.',
  referenceNo: 'Reference No.',
  date: 'Date',
  validUntil: 'Valid Until',
  seller: 'Seller',
  buyer: 'Buyer',
  contactPerson: 'Contact Person',
  address: 'Address',
  country: 'Country',
  telephone: 'Tel',
  email: 'Email',
  website: 'Website',
  no: 'No.',
  product: 'Product',
  sku: 'SKU',
  specification: 'Specification',
  material: 'Material',
  capacity: 'Capacity',
  category: 'Category',
  quantity: 'Quantity',
  unitPrice: 'Unit Price',
  amount: 'Amount',
  finishing: 'Finishing',
  productAmount: 'Product Amount',
  finishingAmount: 'Finishing Amount',
  subtotal: 'Subtotal',
  freight: 'Freight',
  customsClearance: 'Customs Clearance',
  insurance: 'Insurance',
  otherCharges: 'Other Charges',
  total: 'TOTAL',
  paymentTerms: 'Payment Terms',
  incoterm: 'Incoterms®',
  namedPlace: 'Named Place',
  leadTime: 'Lead Time',
  remarks: 'Remarks',
  specialRequirements: 'Special Requirements',
  currency: 'Currency',
  exchangeRate: 'Exchange Rate',
  noImage: 'No Image',
  authorizedBy: 'Authorized By',
  position: 'Position',
  issuedDate: 'Issued Date',
  pageOf: 'Page {current} of {total}',
  pendingConfirmation: 'To be confirmed',
};

const ko: QuotationLabels = {
  documentTitle: {
    preliminary_quote: '예비 견적서',
    final_offer: '최종 견적서',
  },
  quotationNo: '견적 번호',
  referenceNo: '참조 번호',
  date: '발행일',
  validUntil: '유효기간',
  seller: '공급자',
  buyer: '구매자',
  contactPerson: '담당자',
  address: '주소',
  country: '국가',
  telephone: '전화',
  email: '이메일',
  website: '웹사이트',
  no: '번호',
  product: '제품',
  sku: 'SKU',
  specification: '규격',
  material: '소재',
  capacity: '용량',
  category: '카테고리',
  quantity: '수량',
  unitPrice: '단가',
  amount: '금액',
  finishing: '후가공',
  productAmount: '제품 금액',
  finishingAmount: '후가공 금액',
  subtotal: '소계',
  freight: '운임',
  customsClearance: '통관 비용',
  insurance: '보험료',
  otherCharges: '기타 비용',
  total: '합계',
  paymentTerms: '결제 조건',
  incoterm: '인코텀즈®',
  namedPlace: '인도 장소',
  leadTime: '납기',
  remarks: '비고',
  specialRequirements: '특별 요청 사항',
  currency: '통화',
  exchangeRate: '환율',
  noImage: '이미지 없음',
  authorizedBy: '승인자',
  position: '직책',
  issuedDate: '발행일',
  pageOf: '{current} / {total} 페이지',
  pendingConfirmation: '확인 예정',
};

const zhCN: QuotationLabels = {
  documentTitle: {
    preliminary_quote: '初步报价单',
    final_offer: '最终报价单',
  },
  quotationNo: '报价编号',
  referenceNo: '参考编号',
  date: '日期',
  validUntil: '有效期至',
  seller: '卖方',
  buyer: '买方',
  contactPerson: '联系人',
  address: '地址',
  country: '国家',
  telephone: '电话',
  email: '邮箱',
  website: '网站',
  no: '序号',
  product: '产品',
  sku: 'SKU',
  specification: '规格',
  material: '材质',
  capacity: '容量',
  category: '类别',
  quantity: '数量',
  unitPrice: '单价',
  amount: '金额',
  finishing: '表面处理',
  productAmount: '产品金额',
  finishingAmount: '后处理金额',
  subtotal: '小计',
  freight: '运费',
  customsClearance: '清关费用',
  insurance: '保险费',
  otherCharges: '其他费用',
  total: '总计',
  paymentTerms: '付款条件',
  incoterm: '国际贸易术语®',
  namedPlace: '指定地点',
  leadTime: '交货周期',
  remarks: '备注',
  specialRequirements: '特殊要求',
  currency: '货币',
  exchangeRate: '汇率',
  noImage: '暂无图片',
  authorizedBy: '授权人',
  position: '职务',
  issuedDate: '签发日期',
  pageOf: '第 {current} 页，共 {total} 页',
  pendingConfirmation: '待确认',
};

export const QUOTATION_LABELS: Record<DocumentLocale, QuotationLabels> = {
  en,
  ko,
  'zh-CN': zhCN,
};

export function getLabels(locale: DocumentLocale): QuotationLabels {
  return QUOTATION_LABELS[locale] ?? en;
}